import { getPersistentPlayerId } from '../../../api';
import type { DeviceHandoffMethod } from '../../../telemetry/client';
import type { DeviceLockTarget } from '../types/theme';
import type { HandoffTarget } from '../types/deviceFallbackTheme';
import type { IQuickstartTelemetryManager, AnalyticsManagerConfig, BaseAnalyticsProps } from './IQuickstartTelemetryManager';
import * as sessionEvents from './events/sessionAndMatchmakingEvents';
import * as playEvents from './events/playAndCtaEvents';
import * as connectionEvents from './events/connectionEvents';
import * as deviceFallbackEvents from './events/deviceFallbackEvents';
import { sendMilestonePageLoaded, sendMilestoneStreamRendered, sendMilestoneFirstInput } from './milestoneTelemetry';

export class QuickstartTelemetryManager implements IQuickstartTelemetryManager {
  private readonly config: AnalyticsManagerConfig;
  private persistentId: string | undefined;

  constructor(config: AnalyticsManagerConfig) {
    this.config = config;
  }

  public setPersistentId(id: string): void {
    this.persistentId = id;
  }

  private getBaseProperties(): BaseAnalyticsProps | null {
    const state = this.config.getState();
    const persistentId = this.persistentId ?? getPersistentPlayerId();

    if (!state.theme) {
      return null;
    }

    return {
      persistentId,
      sessionId: state.sessionId,
      quickstartSlug: state.theme.slug,
      flow: 'quickstart' as const,
      tier2Filters: ['deaddrop'] as ['deaddrop'],
    };
  }

  private getTotalDuration(): number | undefined {
    const state = this.config.getState();
    return state.pageLoadTime ? Date.now() - state.pageLoadTime : undefined;
  }

  private getPlayerAuthId(): string | undefined {
    const playerAuthId = localStorage.getItem('clerkUserID');
    return playerAuthId || undefined;
  }

  // --- Session & Matchmaking ---

  sendSessionStart(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendSessionStartEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendTrackingPixelFired(trackerUrl: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendTrackingPixelFiredEvent(base, this.getPlayerAuthId(), trackerUrl);
  }

  sendEnvironmentCheckCompleted(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendEnvironmentCheckCompletedEvent(base, this.config.getState(), this.getPlayerAuthId(), this.getTotalDuration());
  }

  sendEmbeddedBrowserBlocked(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendEmbeddedBrowserBlockedEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendDeviceNotSupported(deviceType: 'ios' | 'android' | 'desktop', supportedDevices: { ios?: boolean; android?: boolean; desktop?: boolean }): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendDeviceNotSupportedEvent(base, this.config.getState(), this.getPlayerAuthId(), deviceType, supportedDevices);
  }

  sendOpenInBrowserClicked(browserName: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendOpenInBrowserClickedEvent(base, this.getPlayerAuthId(), browserName);
  }

  sendDeviceFallbackViewed(
    detectedDeviceType: 'ios' | 'android' | 'desktop',
    lockedTo: DeviceLockTarget,
    handoffTarget: HandoffTarget,
  ): void {
    const base = this.getBaseProperties();
    if (!base) return;
    deviceFallbackEvents.sendDeviceFallbackViewedEvent(
      base,
      this.getPlayerAuthId(),
      detectedDeviceType,
      lockedTo,
      handoffTarget,
    );
  }

  sendDeviceHandoffRequested(handoffMethod: DeviceHandoffMethod, detectedDeviceType: 'ios' | 'android' | 'desktop'): void {
    const base = this.getBaseProperties();
    if (!base) return;
    deviceFallbackEvents.sendDeviceHandoffRequestedEvent(base, this.getPlayerAuthId(), handoffMethod, detectedDeviceType);
  }

  sendDeviceHandoffCompleted(
    handoffMethod: DeviceHandoffMethod,
    arrivedDeviceType: 'ios' | 'android' | 'desktop',
    originDeviceType?: 'ios' | 'android' | 'desktop',
  ): void {
    const base = this.getBaseProperties();
    if (!base) return;
    deviceFallbackEvents.sendDeviceHandoffCompletedEvent(
      base,
      this.getPlayerAuthId(),
      handoffMethod,
      arrivedDeviceType,
      originDeviceType,
    );
  }

  sendMatchmakingStarted(matchmakingTimeout: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendMatchmakingStartedEvent(base, this.config.getState(), this.getPlayerAuthId(), this.getTotalDuration(), matchmakingTimeout);
  }

  sendMatchmakingFailed(reason: string, duration: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendMatchmakingFailedEvent(base, this.config.getState(), this.getPlayerAuthId(), this.getTotalDuration(), reason, duration);
  }

  sendMatchmakingSucceeded(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendMatchmakingSucceededEvent(base, this.config.getState(), this.getPlayerAuthId(), this.getTotalDuration());
  }

  sendBroadcastJoinAttempted(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendBroadcastJoinAttemptedEvent(base, this.config.getState(), this.getPlayerAuthId(), this.getTotalDuration());
  }

  sendBroadcastJoinFailed(reason: string, duration: number, errorStage?: 'signaling' | 'sdp' | 'ice' | 'dtls' | 'auth' | 'timeout' | 'unknown'): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendBroadcastJoinFailedEvent(base, this.config.getState(), this.getPlayerAuthId(), this.getTotalDuration(), reason, duration, errorStage);
  }

  sendBroadcastJoinSucceeded(duration: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendBroadcastJoinSucceededEvent(base, this.config.getState(), this.getTotalDuration(), duration);
  }

  sendPlayerLeftBroadcast(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sessionEvents.sendPlayerLeftBroadcastEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  // --- Play & CTA ---

  sendAFKTimerStarted(timeFromJoinMs: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendAFKTimerStartedEvent(base, this.config.getState(), this.getPlayerAuthId(), timeFromJoinMs);
  }

  sendAFKTimeoutReached(sessionDuration: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendAFKTimeoutReachedEvent(base, this.config.getState(), this.getPlayerAuthId(), sessionDuration);
  }

  sendFirstInputDetected(timeToFirstInput: number, inputDeviceType?: 'keyboard' | 'mouse' | 'touch' | 'gamepad'): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendFirstInputDetectedEvent(base, this.config.getState(), this.getPlayerAuthId(), timeToFirstInput, inputDeviceType);
  }

  sendAdTimerStarted(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendAdTimerStartedEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendAdTimerExpired(totalPlaytime: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendAdTimerExpiredEvent(base, this.config.getState(), this.getPlayerAuthId(), totalPlaytime);
  }

  sendWishlistClicked(playtime: number, uiVariant?: string, buttonPosition?: string, elapsedSinceTimerStartSeconds?: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendWishlistClickedEvent(base, this.config.getState(), this.getPlayerAuthId(), playtime, uiVariant, buttonPosition, elapsedSinceTimerStartSeconds);
  }

  sendCTAPageViewed(totalSessionDuration: number, ctaType?: string, uiVariant?: string, referrerPage?: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendCTAPageViewedEvent(base, this.config.getState(), this.getPlayerAuthId(), totalSessionDuration, ctaType, uiVariant, referrerPage);
  }

  sendWishlistCTAClicked(
    steamUrl: string,
    triggerSource?: 'cta_page' | 'thank_you' | 'matchmaking_failed' | 'broadcast_join_failed' | 'error' | 'auth_failed' | 'auto_redirect' | 'afk_timer_hit' | 'maxed_out_game_time' | 'loading_timeout',
    providerInferred?: string, providerSupported?: boolean, providerSelected?: string, dedupRuleApplied?: boolean,
  ): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendWishlistCTAClickedEvent(base, this.config.getState(), this.getPlayerAuthId(), steamUrl, triggerSource, providerInferred, providerSupported, providerSelected, dedupRuleApplied);
  }

  sendAuthAttempted(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendAuthAttemptedEvent(base, this.config.getState());
  }

  sendAuthFailed(authProvider: string, reason: string, attemptId?: string, failureReason?: 'denied' | 'closed' | 'network' | 'provider_error' | 'timeout' | 'unknown', errorCode?: string, errorMessage?: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendAuthFailedEvent(base, this.config.getState(), authProvider, reason, attemptId, failureReason, errorCode, errorMessage);
  }

  sendAuthSucceeded(authProvider: string, playerAuthId: string, attemptId?: string, accountState?: 'signup' | 'login'): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendAuthSucceededEvent(base, authProvider, playerAuthId, attemptId, accountState);
  }

  sendThankYouPageViewed(playerAuthId: string, uiVariant?: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendThankYouPageViewedEvent(base, this.config.getState(), playerAuthId, uiVariant);
  }

  sendWishlistEmailSent(playerAuthId: string, email: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendWishlistEmailSentEvent(base, playerAuthId, email);
  }

  sendDeveloperLogoClicked(destinationId: string, openBehavior: 'new_tab' | 'same_tab', playerAuthId?: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendDeveloperLogoClickedEvent(base, this.config.getState(), this.getPlayerAuthId(), destinationId, openBehavior, playerAuthId);
  }

  sendThankYouRedirect(steamUrl: string, userAuthId?: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendThankYouRedirectEvent(base, steamUrl, userAuthId);
  }

  sendPlayerExited(reason: 'close' | 'navigation' | 'timeout' | 'error' | 'unknown', sessionDuration?: number, totalSessionDuration?: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendPlayerExitedEvent(base, this.config.getState(), this.getPlayerAuthId(), reason, sessionDuration, totalSessionDuration);
  }

  sendTabVisibilityChanged(visibilityState: 'unfocus' | 'focus', sessionDuration?: number, totalSessionDuration?: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    playEvents.sendTabVisibilityChangedEvent(base, this.config.getState(), this.getPlayerAuthId(), visibilityState, sessionDuration, totalSessionDuration);
  }

  // --- Connection ---

  sendWebSocketConnectionStarted(isFastStart: boolean): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendWebSocketConnectionStartedEvent(base, this.getPlayerAuthId(), isFastStart);
  }

  sendWebSocketReady(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendWebSocketReadyEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendOAuthReturnDetected(hasAuthId: boolean): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendOAuthReturnDetectedEvent(base, this.config.getState(), this.getPlayerAuthId(), hasAuthId);
  }

  sendLobbyCreationStarted(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendLobbyCreationStartedEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendPeerIdAcquired(peerId: string): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendPeerIdAcquiredEvent(base, this.config.getState(), this.getPlayerAuthId(), peerId);
  }

  sendSdpOfferSent(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendSdpOfferSentEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendSdpAnswerReceived(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendSdpAnswerReceivedEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendIceCandidatesSent(candidateCount: number): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendIceCandidatesSentEvent(base, this.config.getState(), this.getPlayerAuthId(), candidateCount);
  }

  sendIceCheckingStarted(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendIceCheckingStartedEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendIceConnected(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendIceConnectedEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  sendRemoteStreamReceived(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    connectionEvents.sendRemoteStreamReceivedEvent(base, this.config.getState(), this.getPlayerAuthId());
  }

  // --- Milestones (Zod-validated, no tier filters) ---

  sendMilestonePageLoaded(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sendMilestonePageLoaded(base, this.config.getState());
  }

  sendMilestoneStreamRendered(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sendMilestoneStreamRendered(base, this.config.getState());
  }

  sendMilestoneFirstInput(): void {
    const base = this.getBaseProperties();
    if (!base) return;
    sendMilestoneFirstInput(base, this.config.getState());
  }

  cleanup(): void {
    // No-op
  }
}
